"use client";

import { Check, LoaderCircle } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";

import { cn } from "@/lib/utils";

import { EASE_OUT_EXPO } from "../../motion/reveal";
import { TextRoll } from "../../motion/text-roll";

// =============================================================================

export type SubmitState = "idle" | "loading" | "success";

type SubmitButtonProps = {
  state: SubmitState;
  label: string;
  loadingLabel: string;
  successLabel: string;
  className?: string;
};

const swap = {
  initial: { y: "110%", opacity: 0 },
  animate: { y: "0%", opacity: 1 },
  exit: { y: "-110%", opacity: 0 },
  transition: { duration: 0.55, ease: EASE_OUT_EXPO },
};

/**
 * Full-width submit button of the forms. On hover the background fills from
 * the bottom and the letters roll; the label slides to a spinner while the
 * form is sent, then to a check once it succeeded.
 */
export function SubmitButton({
  state,
  label,
  loadingLabel,
  successLabel,
  className,
}: SubmitButtonProps) {
  const isBusy = state !== "idle";

  return (
    <motion.button
      type="submit"
      aria-disabled={isBusy || undefined}
      aria-busy={state === "loading" || undefined}
      initial="rest"
      whileHover={isBusy ? undefined : "hover"}
      animate="rest"
      onClick={(event) => {
        // aria-disabled keeps the focus on the button while sending
        if (isBusy) event.preventDefault();
      }}
      className={cn(
        "group/submit relative isolate flex h-[clamp(3.75rem,5.4vw,4.875rem)] w-full items-center justify-center overflow-hidden bg-surface-3 text-[clamp(1.0625rem,1.5vw,1.375rem)] text-foreground shadow-[inset_0_1px_0_rgb(255_255_255/0.05)] transition-colors duration-500",
        isBusy && "cursor-progress",
        className
      )}
    >
      {/* Fill rising from the bottom on hover */}
      <span
        aria-hidden
        className={cn(
          "absolute inset-0 -z-10 origin-bottom scale-y-0 bg-foreground transition-transform duration-[650ms] ease-out-expo",
          !isBusy && "group-hover/submit:scale-y-100"
        )}
      />

      <span className="relative grid h-[1.6em] place-items-center overflow-hidden">
        <AnimatePresence initial={false} mode="popLayout">
          {state === "idle" && (
            <motion.span
              key="idle"
              {...swap}
              className="col-start-1 row-start-1 transition-colors duration-500 group-hover/submit:text-background"
            >
              <TextRoll>{label}</TextRoll>
            </motion.span>
          )}
          {state === "loading" && (
            <motion.span
              key="loading"
              {...swap}
              className="col-start-1 row-start-1 inline-flex items-center gap-3"
            >
              <LoaderCircle aria-hidden className="size-5 animate-spin" />
              {loadingLabel}
            </motion.span>
          )}
          {state === "success" && (
            <motion.span
              key="success"
              {...swap}
              className="col-start-1 row-start-1 inline-flex items-center gap-3"
            >
              <Check aria-hidden className="size-5 text-brand" />
              {successLabel}
            </motion.span>
          )}
        </AnimatePresence>
      </span>
    </motion.button>
  );
}

// =============================================================================
